import classNames from './CommonMistakes.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import Layer from '@/assets/svg/Layer_1.svg';
import { useCallback, useEffect, useState } from 'react';
import RightArrow from '@/assets/svg/black-right-arrow.svg';
import thumbDown from '@/assets/svg/dont-know-highlight.svg';
import thumbUp from '@/assets/svg/know-highlight.svg';
import UseScreenSize from '@/hooks/UseScreenSize';
import { GetMistakesResponse } from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import TutorTopicsUtils, { QuizStatus } from '@/utils/tutorTopics';
import { useNavigate } from 'react-router-dom';

interface CommonMistakesProps {
  mistakes: GetMistakesResponse[];
  onSelect: (
    enhancementArea: string,
    enhancementAreaSimplified: string,
    status: QuizStatus
  ) => void;
  headerCount?: number;
}

const CommonMistakes = ({ mistakes, onSelect, headerCount }: CommonMistakesProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = UseScreenSize();
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToChat = useCallback(() => {
    navigate('/dashboard/my-tutor-chat');
  }, [navigate]);
  useEffect(() => {
    setCurrentIndex(prev => Math.min(prev, Math.max(0, mistakes.length - 1)));
  }, [mistakes.length]);

  if (mistakes.length === 0) {
    return (
      <div className={classes(classNames.commonMistake, { [classNames.mobileCard]: isMobile })}>
        <div className={classNames.emptyStateContent}>
          <div className={classNames.emptyStateMessage}>{t('my_tutor_page.noActivities')}</div>
          <button onClick={goToChat} className={classNames.learn}>
            {t('my_tutor_page.practiceBtn')} {'>'}
          </button>
        </div>
      </div>
    );
  }

  const mistake = mistakes[currentIndex];
  const status = TutorTopicsUtils.getQuizStatus(mistake.quizzes[0].Score);

  const onLearnClick = () => {
    onSelect(
      mistake.enhancementArea,
      mistake.enhancementAreaSimplified || mistake.enhancementArea,
      status
    );
  };

  const cardContent = (
    <div className={classes(classNames.commonMistake, { [classNames.mobileCard]: isMobile })}>
      {!isMobile && (
        <div className={classNames.header}>
          <div className={classNames.icon}>
            {status === QuizStatus.PRACTICE && '💡'}
            {status === QuizStatus.IMPROVEMENT && '🚀'}
            {status === QuizStatus.COMPLETED && '✅'}
          </div>
          <div
            className={classes(classNames.quizStatus, {
              [classNames.quizStatusPractice]: status === QuizStatus.PRACTICE,
              [classNames.quizStatusImprovement]: status === QuizStatus.IMPROVEMENT,
              [classNames.quizStatusCompleted]: status === QuizStatus.COMPLETED,
            })}
          >
            {t(`my_tutor_page.quizStatus.${status}`)}
          </div>
        </div>
      )}
      <div className={classNames.topic}>
        {mistake.enhancementAreaSimplified || mistake.enhancementArea}
      </div>
      <div className={classNames.description}>{mistake.description}</div>
      <div className={classNames.examplesSection}>
        {mistake.examples?.length
          ? (() => {
              const example = mistake.examples[0];
              return (
                <div className={classNames.examplePair}>
                  {(Array.isArray(example) ? example : [example]).map(
                    (text, textIndex) =>
                      text && (
                        <div
                          key={textIndex}
                          className={classes(classNames.examplePill, {
                            [classNames.wrongPill]: textIndex === 0,
                            [classNames.correctPill]: textIndex > 0,
                          })}
                        >
                          <span className={classNames.pillIconWrapper}>
                            <SVG
                              src={textIndex === 0 ? thumbDown : thumbUp}
                              className={classNames.pillIcon}
                            />
                          </span>
                          <span className={classNames.pillText}>{text}</span>
                        </div>
                      )
                  )}
                </div>
              );
            })()
          : null}
      </div>
      <div className={classNames.learnParent}>
        {!isMobile && (
          <span className={classNames.cardCounter}>
            {currentIndex + 1}/{mistakes.length}
          </span>
        )}
        {status === QuizStatus.PRACTICE && mistake.examples?.length && (
          <button onClick={onLearnClick} className={classNames.learn}>
            {t('my_tutor_page.practiceBtn')} {'>'}
          </button>
        )}
        {status === QuizStatus.IMPROVEMENT && (
          <>
            <button onClick={onLearnClick} className={classNames.learn}>
              {t(
                (mistake.numberOfQuizzes || 0) === 1
                  ? 'my_tutor_page.learn1'
                  : 'my_tutor_page.learnN',
                { totalLessonsCount: mistake.numberOfQuizzes }
              )}
              <SVG src={Layer} />
            </button>
            <span className={classNames.score}>
              {t('my_tutor_page.score')}: {mistake.score}
            </span>
          </>
        )}
        {status === QuizStatus.COMPLETED && (
          <span className={classNames.completedActivities}>
            {t('my_tutor_page.completedActivities', {
              numberOfCompletedActivities: mistake.numberOfQuizzes,
            })}
          </span>
        )}
      </div>
    </div>
  );
  if (isMobile) {
    return cardContent;
  }
  return (
    <div className={classNames.outerWrapper}>
      {headerCount !== undefined && headerCount > 0 && (
        <div className={classNames.outerHeader}>
          {t(
            (headerCount ?? 0) > 1
              ? 'my_tutor_page.practiceCountPlural'
              : 'my_tutor_page.practiceCount',
            { count: headerCount }
          )}
        </div>
      )}
      <div className={classNames.cardWrapper}>
        {cardContent}
        {mistakes.length > 1 && (
          <button
            className={classNames.nextCardBtn}
            onClick={() => setCurrentIndex(i => (i + 1) % mistakes.length)}
            aria-label='Next activity'
          >
            <SVG src={RightArrow} />
          </button>
        )}
      </div>
    </div>
  );
};
export default CommonMistakes;
